
/**
 * Handles the show submit page
 */
import { initializeFirebase, isEmailSignInLink, createPageLoaderManager, getUrlParams, cleanUrlParams } from './utils.js';

(function($) {
  // Initialize Firebase app with configuration from WordPress
  // The site object is localized from WordPress via wp_localize_script
  // Use shared utility function to handle initialization safely
  initializeFirebase('show-submit.js');

  let currentFirebaseUser = null;

  // Flag to track if payment success has been handled
  // This prevents multiple executions if auth state changes multiple times
  let paymentSuccessHandled = false;

  // Check if the current URL is a Firebase email sign-in link
  // This detects when user is returning from clicking a sign-in link in their email
  // Use shared utility function
  const isEmailLinkFlow = isEmailSignInLink();

  // Track if we're in an email link sign-in flow
  // This flag persists even if Firebase cleans the URL before events fire
  // Set to true if email link is detected, cleared when userDataLoaded fires
  let isProcessingEmailLink = isEmailLinkFlow;

  // UI state constants for managing element visibility
  // These represent the different authentication and credits states
  const UI_STATES = {
    LOADING: 'loading',
    LOGGED_OUT: 'loggedOut',
    LOGGED_IN_NO_CREDITS: 'loggedInNoCredits',
    LOGGED_IN_HAS_CREDITS: 'loggedInHasCredits',
    NOT_PAYWALLED: 'notPaywalled',
  };

  /**
   * Sets the UI state based on authentication and credits status.
   * Centralizes all show/hide logic for the paywall-related elements.
   *
   * @param {string} state - One of UI_STATES values
   */
  function setUIState(state) {
    // If loading state, hide all containers and show loader
    // This prevents flashing of wrong states during email link sign-in
    if (state === UI_STATES.LOADING) {
      $('#firebase-email-link-auth-container').hide();
      $('.signed-in-as-container').hide();
      $('#login-and-purchase-credits-container').hide();
      $('#submit-instructions-container').hide();
      $('#paywall-notice-container').hide();
      $('#purchaseShowSubmissionCreditsButton').hide();
      $('#purchaseShowSubmissionCreditsButtonTwo').hide();
      $('#show-submit').hide();
      return;
    }

    // Element visibility mapping for each state
    // Each state defines which elements should be shown or hidden
    const stateConfig = {
      [UI_STATES.NOT_PAYWALLED]: {
        '#firebase-email-link-auth-container': 'hide',
        '.signed-in-as-container': 'hide',
        '#login-and-purchase-credits-container': 'hide',
        '#submit-instructions-container': 'show',
        '#paywall-notice-container': 'hide',
        '#purchaseShowSubmissionCreditsButton': 'hide',
        '#purchaseShowSubmissionCreditsButtonTwo': 'hide',
        '#show-submit': 'show'
      },
      [UI_STATES.LOGGED_OUT]: {
        '#firebase-email-link-auth-container': 'show',
        '.signed-in-as-container': 'hide',
        '#login-and-purchase-credits-container': 'show',
        '#submit-instructions-container': 'hide',
        '#paywall-notice-container': 'hide',
        '#purchaseShowSubmissionCreditsButton': 'hide',
        '#purchaseShowSubmissionCreditsButtonTwo': 'hide',
        '#show-submit': 'hide'
      },
      [UI_STATES.LOGGED_IN_NO_CREDITS]: {
        '#firebase-email-link-auth-container': 'hide',
        '.signed-in-as-container': 'show',
        '#login-and-purchase-credits-container': 'hide',
        '#submit-instructions-container': 'hide',
        '#paywall-notice-container': 'show',
        '#purchaseShowSubmissionCreditsButton': 'show',
        '#purchaseShowSubmissionCreditsButtonTwo': 'hide',
        '#show-submit': 'hide'
      },
      [UI_STATES.LOGGED_IN_HAS_CREDITS]: {
        '#firebase-email-link-auth-container': 'hide',
        '.signed-in-as-container': 'show',
        '#login-and-purchase-credits-container': 'hide',
        '#submit-instructions-container': 'show',
        '#paywall-notice-container': 'hide',
        '#purchaseShowSubmissionCreditsButton': 'hide',
        '#purchaseShowSubmissionCreditsButtonTwo': 'show',
        '#show-submit': 'show'
      }
    };

    const config = stateConfig[state];
    if (!config) return;

    // Apply visibility settings using jQuery show/hide
    // This centralizes all element visibility logic in one place
    Object.entries(config).forEach(([selector, action]) => {
      const $el = $(selector);
      if (action === 'show') {
        $el.show();
      } else {
        $el.hide();
      }
    });
  }

  /**
   * Calculate UI state based on current conditions
   * This is a pure function that determines which UI state to show
   * @param {Object} options - State calculation inputs
   * @param {boolean} options.hasAuthResolved - Whether Firebase auth state is known
   * @param {boolean} options.isAuthenticated - User is signed in
   * @param {number} options.submissionCredits - User's submission credits count
   * @param {boolean} options.freeSubmissions - User has free submissions
   * @param {boolean} options.isPaywalled - Submissions are paywalled
   * @returns {string} The calculated UI state constant
   */
  function calculateUIState(options) {
    const { hasAuthResolved, isAuthenticated, submissionCredits, freeSubmissions, isPaywalled } = options;

    // If not paywalled, show form immediately
    // This allows users to submit without authentication
    if (!isPaywalled) {
      return UI_STATES.NOT_PAYWALLED;
    }

    // Stay in loading state until Firebase auth resolves
    // This prevents showing content before we know the user's auth state
    if (!hasAuthResolved) {
      return UI_STATES.LOADING;
    }

    // User is authenticated - check credits status
    if (isAuthenticated) {
      if (freeSubmissions || submissionCredits > 0) {
        return UI_STATES.LOGGED_IN_HAS_CREDITS;
      }
      return UI_STATES.LOGGED_IN_NO_CREDITS;
    }

    // User is not authenticated - show login form
    return UI_STATES.LOGGED_OUT;
  }

  /**
   * Update signed-in user display
   * Updates all elements with the signed-in user information
   * @param {Object} userData - User data object
   */
  function updateSignedInDisplay(userData) {
    if (!userData) return;

    $('.signed-in-as').text(userData.email);
    $('.signed-in-as-container').removeClass('d-none');

    if (userData.free_submissions) {
      $('.signed-in-as-credits').text('Unlimited');
    } else {
      $('.signed-in-as-credits').text(userData.submission_credits || 0);
    }

    // Update contact email field
    // This field is readonly when user is signed in
    $('#contact-email').prop('readonly', true).prop('disabled', true).val(userData.email);
  }

  /**
   * Reset signed-in display when user logs out
   * Clears user information and re-enables form fields
   */
  function resetSignedInDisplay() {
    $('.signed-in-as-container').addClass('d-none');
    $('#contact-email').prop('readonly', false).prop('disabled', false).val('');
  }

  // Create page loader manager using shared utility
  // This ensures the loader is only hidden once, preventing race conditions
  const pageLoader = createPageLoaderManager();

  // if (site.env === 'local') {
  //   auth.useEmulator('http://127.0.0.1:9099');
  // }

  // Handle payment success verification
  // This function verifies the Stripe checkout session and refreshes user credits
  // It requires an authenticated user and access token, so it's called from onAuthStateChanged
  // sessionId: The Stripe checkout session ID from the URL query parameter
  // accessToken: The Firebase auth token for API authentication
  function handlePaymentSuccess(sessionId, accessToken) {
    // Prevent multiple executions if auth state changes multiple times
    if (paymentSuccessHandled) {
      return;
    }
    paymentSuccessHandled = true;

    // Show loading state
    console.log('Verifying checkout session:', sessionId);

    // First, verify the checkout session to trigger fulfillment
    // This calls fulfill_checkout_session_if_needed() which handles idempotent order fulfillment
    $.ajax({
      url: '/wp-json/showlist/v1/stripe/verify-checkout-session',
      method: 'POST',
      headers: {
        'Authorization': 'Bearer ' + accessToken,
        'Content-Type': 'application/json'
      },
      data: JSON.stringify({
        session_id: sessionId
      }),
      success: function(verifyResponse) {

        // After successful verification, refresh user credits
        // This ensures the credits display shows the updated balance
        $.ajax({
          url: '/wp-json/showlist/v1/firebase-users/me',
          method: 'GET',
          headers: {
            'Authorization': 'Bearer ' + accessToken
          },
          success: function(userResponse) {
            // Update the current user object and credits display
            currentFirebaseUser = userResponse;

            if (currentFirebaseUser.free_submissions) {
              $('.signed-in-as-credits').text('Unlimited');
            } else {
              $('.signed-in-as-credits').text(currentFirebaseUser.submission_credits || 0);
            }

            // Show success message
            alert('Payment successful! Your credits have been added to your account.');

            // Set UI state to logged in with credits after successful payment
            // This centralizes all show/hide logic into a single function call
            setUIState(UI_STATES.LOGGED_IN_HAS_CREDITS);

            // Clean up URL by removing query parameters
            // This prevents the success message from showing again on refresh
            window.history.replaceState({}, document.title, window.location.pathname);
          },
          error: function(xhr, status, error) {
            console.error('Error refreshing user credits:', error);
            alert('Payment verified but failed to refresh credits display. Please refresh the page.');
            // Clean up URL
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        });
      },
      error: function(xhr, status, error) {
        console.error('Error verifying checkout session:', error);
        alert('Payment completed but verification failed. Your credits may take a moment to appear. Please refresh the page.');
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    });
  }

  /**
   * Handle user data loaded event
   * This is emitted by FirebaseEmailLinkAuth when user signs in and data is fetched
   * Fired when user is authenticated AND user data is fetched from API
   * @param {CustomEvent} event - The custom event with user data
   */
  document.addEventListener('firebase:userDataLoaded', function(event) {
    const { user, userData } = event.detail;

    currentFirebaseUser = userData;

    // Clear email link processing flag now that we have user data
    // This allows future userDataCleared events to render normally
    isProcessingEmailLink = false;

    // Update signed-in display with user information
    updateSignedInDisplay(userData);

      // Check for payment success query parameters
      // This handles the redirect back from Stripe Checkout when payment is successful
      // Use shared utility function to get URL parameters
      var urlParams = getUrlParams();
      var paymentStatus = urlParams.get('payment');
      var sessionId = urlParams.get('session_id');

    // If payment was successful and we have a session ID, verify the checkout session
    if (paymentStatus === 'success' && sessionId && !paymentSuccessHandled) {
      user.getIdToken().then(function(accessToken) {
        handlePaymentSuccess(sessionId, accessToken);
      });
    }

    // Calculate and set UI state based on user's credits status
    // This centralizes state calculation logic
    const state = calculateUIState({
      hasAuthResolved: true,
      isAuthenticated: true,
      submissionCredits: userData.submission_credits || 0,
      freeSubmissions: userData.free_submissions || false,
      isPaywalled: site.acfOptions.is_submissions_paywalled
    });
    setUIState(state);
    pageLoader.hide();
  });

  /**
   * Handle user data cleared event
   * This is emitted by FirebaseEmailLinkAuth when user signs out
   * Fired when no user is found or user signs out
   * IMPORTANT: During email link sign-in flow, this can fire BEFORE userDataLoaded
   * We must check for email link flow and skip rendering to prevent flashing wrong state
   * @param {CustomEvent} event - The custom event
   */
  document.addEventListener('firebase:userDataCleared', function(event) {
    // If we're processing an email link sign-in flow, don't render yet
    // The userDataLoaded event will fire next and render the correct state
    // This prevents flashing loggedOut before loggedInHasCredits
    // We use isProcessingEmailLink flag instead of isEmailLinkFlow because
    // Firebase may clean the URL before this event fires
    if (isProcessingEmailLink) {
      console.log('show-submit.js: Skipping render during email link flow - waiting for userDataLoaded');
      return;
    }

    currentFirebaseUser = null;

    // Reset signed-in display when user logs out
    resetSignedInDisplay();

    // Calculate and set UI state to logged out
    const state = calculateUIState({
      hasAuthResolved: true,
      isAuthenticated: false,
      submissionCredits: 0,
      freeSubmissions: false,
      isPaywalled: site.acfOptions.is_submissions_paywalled
    });
    setUIState(state);
    pageLoader.hide();
  });

  /**
   * Handle user data error event
   * This is emitted by FirebaseEmailLinkAuth when API fetch fails
   * Fired if API call to fetch user data fails
   * @param {CustomEvent} event - The custom event with error details
   */
  document.addEventListener('firebase:userDataError', function(event) {
    console.error('show-submit.js: Error loading user data', event.detail.error);

    // Still render - show logged out state as fallback
    // This allows users to still interact with the page even if data fetch fails
    const state = calculateUIState({
      hasAuthResolved: true,
      isAuthenticated: false,
      submissionCredits: 0,
      freeSubmissions: false,
      isPaywalled: site.acfOptions.is_submissions_paywalled
    });
    setUIState(state);
    pageLoader.hide();
  });

	$(document).ready(function() {

    /**
     * Firebase Authentication / Payment Success Handling
     *
     * This handles the Firebase authentication and payment success handling.
     * It checks for payment success query parameters and verifies the checkout session.
     * It also refreshes the user credits display.
     */

    // Check if firebaseEmailLinkAuth has already loaded user data
    // This handles the race condition where auth resolved before DOMContentLoaded
    // If auth has already resolved, we can immediately render the correct state
    if (window.firebaseEmailLinkAuth && window.firebaseEmailLinkAuth.hasLoadedUserData) {
      const userData = window.firebaseEmailLinkAuth.currentUserData;
      const user = window.firebaseEmailLinkAuth.currentUser;

      if (user && userData) {
        currentFirebaseUser = userData;
        updateSignedInDisplay(userData);

        const state = calculateUIState({
          hasAuthResolved: true,
          isAuthenticated: true,
          submissionCredits: userData.submission_credits || 0,
          freeSubmissions: userData.free_submissions || false,
          isPaywalled: site.acfOptions.is_submissions_paywalled
        });
        setUIState(state);
        pageLoader.hide();
      }
    } else if (isEmailLinkFlow) {
      // User is returning from email sign-in link
      // Stay in loading state until Firebase processes the link and fires events
      // This prevents flashing the wrong state before sign-in completes
      // The firebase:userDataLoaded event will update the state when sign-in completes
      console.log('show-submit.js: Email sign-in link detected, waiting for Firebase to process...');
      setUIState(UI_STATES.LOADING);
    }
    // Otherwise, stay in loading state - Firebase events will fire and update the state
    // The event handlers above will call setUIState() when auth resolves

    if (site.acfOptions.is_submissions_paywalled) {


      // Disable purchase button initially until a package is selected
      // This prevents users from attempting to purchase without selecting a credits package
      $('#confirmPurchaseShowSubmissionCreditsButton').prop('disabled', true);

      // Handle price selection dropdown change
      // Enable/disable the purchase button based on whether a valid price is selected
      $('#stripe-product-prices-select').on('change', function() {
        var selectedPriceId = $(this).val();
        var $purchaseButton = $('#confirmPurchaseShowSubmissionCreditsButton');

        // Enable button if a valid price is selected (not empty and not the placeholder)
        // Disable button if no selection or placeholder is selected
        if (selectedPriceId && selectedPriceId !== '' && selectedPriceId !== null) {
          $purchaseButton.prop('disabled', false);
        } else {
          $purchaseButton.prop('disabled', true);
        }
      });

      // Handle modal closing - blur focused elements to prevent accessibility warnings
      // This prevents aria-hidden warnings when the modal closes with a focused element
      $('#purchaseShowSubmissionCreditsModal').on('hide.bs.modal', function() {
        // Blur any focused elements within the modal before it's hidden
        // This prevents accessibility warnings about aria-hidden on focused elements
        var $modal = $(this);
        var $focusedElement = $modal.find(':focus');
        if ($focusedElement.length) {
          $focusedElement.blur();
        }
      });

      // Reset purchase button state when modal is closed
      // This ensures the button is disabled when the modal is reopened
      $('#purchaseShowSubmissionCreditsModal').on('hidden.bs.modal', function() {
        $('#stripe-product-prices-select').val('').trigger('change');
        $('#confirmPurchaseShowSubmissionCreditsButton').prop('disabled', true);
      });

      // Handle purchase show submission credits button click
      // This creates a Stripe checkout session and redirects the user to Stripe Checkout
      $('#confirmPurchaseShowSubmissionCreditsButton').on('click', function() {
        // Get the selected price ID from the dropdown
        // The dropdown contains Stripe price IDs as values
        var selectedPriceId = $('#stripe-product-prices-select').val();

        // Validate that a price has been selected
        // Show an error if no price is selected
        if (!selectedPriceId || selectedPriceId === '' || selectedPriceId === null) {
          alert('Please select a credits package before purchasing.');
          return;
        }

        // Check if user is authenticated
        // We need a Firebase user to create a checkout session
        var currentUser = firebase.auth().currentUser;
        if (!currentUser) {
          alert('Please sign in to purchase credits.');
          return;
        }

        // Disable the button and show loading state
        // This prevents multiple clicks and provides user feedback
        var $button = $(this);
        var originalButtonText = $button.text();
        $button.prop('disabled', true).text('Processing...');

        // Get Firebase auth token for API authentication
        // This token is required by the backend to verify the user's identity
        currentUser.getIdToken().then(function(accessToken) {
          // Construct absolute URLs for success and cancel redirects
          // Stripe requires absolute URLs for redirect purposes
          var currentUrl = window.location.origin + window.location.pathname;
          var successUrl = currentUrl + '?payment=success&session_id={CHECKOUT_SESSION_ID}';
          var cancelUrl = currentUrl + '?payment=cancelled&session_id={CHECKOUT_SESSION_ID}';

          // Create the checkout session via API
          // This endpoint creates a Stripe checkout session and returns the checkout URL
          $.ajax({
            url: '/wp-json/showlist/v1/stripe/checkout-session',
            method: 'POST',
            headers: {
              'Authorization': 'Bearer ' + accessToken,
              'Content-Type': 'application/json'
            },
            data: JSON.stringify({
              price_id: selectedPriceId,
              success_url: successUrl,
              cancel_url: cancelUrl,
              additional_metadata: {
                is_credits_purchase: true,
              }
            }),
            success: function(response) {
              // On successful session creation, redirect to Stripe Checkout
              // The response contains the checkout URL from Stripe
              if (response && response.url) {
                window.location.href = response.url;
              } else {
                // If response doesn't have URL, show error
                alert('Error: Invalid response from server. Please try again.');
                $button.prop('disabled', false).text(originalButtonText);
              }
            },
            error: function(xhr) {
              // Handle API errors
              // Show user-friendly error message
              var errorMessage = 'An error occurred while creating the checkout session.';
              if (xhr.responseJSON && xhr.responseJSON.message) {
                errorMessage = xhr.responseJSON.message;
              } else if (xhr.status === 401) {
                errorMessage = 'Please sign in to purchase credits.';
              } else if (xhr.status === 400) {
                errorMessage = 'Invalid request. Please check your selection and try again.';
              }
              alert(errorMessage);
              // Re-enable the button
              $button.prop('disabled', false).text(originalButtonText);
            }
          });
        }).catch(function(error) {
          // Handle Firebase token retrieval errors
          console.error('Error getting Firebase token:', error);
          alert('Error authenticating. Please try signing in again.');
          $button.prop('disabled', false).text(originalButtonText);
        });
      });

      // Handle payment cancellation
      // Check for query parameters indicating payment cancellation
      // This runs immediately on page load since it doesn't require authentication
      function handlePaymentCancelled() {
        // Use shared utility function to get URL parameters
        var urlParams = getUrlParams();
        var paymentStatus = urlParams.get('payment');

        if (paymentStatus === 'cancelled') {
          // Payment was cancelled
          // Show cancellation message
          alert('Payment was cancelled. No charges were made.');

          // Clean up URL by removing query parameter using shared utility
          cleanUrlParams(['payment']);
        }
        // Note: Payment success handling is done in onAuthStateChanged callback
        // to ensure the user is authenticated before attempting verification
      }

      // Check for payment result on page load
      // This handles the redirect back from Stripe Checkout
      handlePaymentCancelled();
    }

    /**
     * Show Submission Form Handling
     */

		// add strict url validator to parsley
		window.Parsley.addValidator('urlStrict', {
		  validate: function(value) {
				var regExp = /(https?:\/\/)(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#()?&//=]*)/;
				return regExp.test( value );
		  },
		  messages: {
	      en: 'Please provide a valid URL (must include http:// or https://)'
		  }
		})

		// Parsely Form Validation
		$(".parsely-form").parsley({
			errorClass: 'is-invalid text-danger',
			successClass: '',
			errorsWrapper: '<span class="form-text text-danger"></span>',
			errorTemplate: '<span></span>',
			trigger: 'change',
			requiredMessage: 'Required',
		});

		$('#venue').on('change', function() {
			if ($(this).val() === 'other' ) {
				$('.other-venue-info').show();
				$('#other-venue').attr('required', true);
				$('#other-venue-street-address').attr('required', true);
			} else {
				$('.other-venue-info').hide();
				$('#other-venue').attr('required', false);
				$('#other-venue-street-address').attr('required', false);
			}
		});

		$('.form-select').on('change', function() {
      // Get the current value and check if it exists and has length
      // This prevents errors when the value is null (e.g., when modal closes)
      var currentValue = $(this).val();
      if (currentValue && currentValue.length) {
        $(this).addClass('text-dark');
      }
		});

    // Submit Form
    function isSubmitting(isSubmitting) {
      if (isSubmitting) {
        $('.btn-spinner').show();
        $('.btn-submit').attr('disabled', true);
      } else {
        $('.btn-spinner').hide();
        $('.btn-submit').attr('disabled', false);
      }
    }

		$('#show-submit').submit(async function(e) {
			e.preventDefault();
			isSubmitting(true);

			var $form = $(this);
			var formData = $form.serializeArray();

      const requestOptions = {
        headers: {}
      };

      if (site.acfOptions.is_submissions_paywalled) {
        /// get the firebase access token
        const accessToken = await firebase.auth().currentUser.getIdToken();
        requestOptions.headers['Authorization'] = 'Bearer ' + accessToken;

        formData.push({
          name: 'contact-email',
          value: currentFirebaseUser.email
        });
      }

      // convert formData to a JSON object
      const jsonData = {};
      formData.forEach(function(item) {
        jsonData[item.name] = decodeURIComponent(item.value);
      });

      $.ajax({
        url: '/wp-json/showlist/v1/submit',
        method: 'POST',
        contentType: "application/json; charset=utf-8",
        data: JSON.stringify(jsonData),
        headers: requestOptions.headers,
        success: function() {
          $('.submit-a-show').toggleClass('d-none');
          $('.submit-thank-you').toggleClass('d-none');
        },
        error: function(error) {
          if (error.responseJSON?.code === 'possible_duplicate_show') {
            $('.submit-error-message').html(error.responseJSON?.message + ' If you think this is a mistake, please <a href="mailto:' + site.submission_published_email_reply_to_address + '">contact us</a>.');
          }
          $('.submit-a-show').toggleClass('d-none');
          $('.submit-error').toggleClass('d-none');
        }
      });
		});

    if (!(site.acfOptions.is_submissions_paywalled && site.acfOptions.enable_stripe_integration)) {
      setUIState(UI_STATES.NOT_PAYWALLED);
      pageLoader.hide();
    }

    // Fallback timeout: hide loader after 3s even if auth events never fire
    // This is a safety net in case Firebase events fail to fire (network error, etc.)
    setTimeout(function() {
      pageLoader.hide();
    }, 3000);
	});
})(jQuery);
